import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Trash2, KeyRound } from 'lucide-react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import {
  beginRegisterPasskey,
  deletePasskey,
  finishRegisterPasskey,
  listPasskeys,
  regenerateRecoveryCodes,
  type Passkey,
} from '../api/passkeys';
import { ApiError } from '../api/client';
import { getApiClient } from '../api/factory';
import { useT } from '../i18n';
import { RecoveryCodesDialog } from './RecoveryCodesDialog';

// Блок «Двухфакторная аутентификация» в SettingsPage. Содержит список
// passkey'ев, кнопку регистрации, regenerate recovery codes.

export function PasskeysSection() {
  const t = useT();
  const qc = useQueryClient();
  const supported = browserSupportsWebAuthn();
  const [error, setError] = useState<string | null>(null);
  const [previewCodes, setPreviewCodes] = useState<string[] | null>(null);

  const passkeysQuery = useQuery({
    queryKey: ['passkeys'],
    queryFn: () => listPasskeys(getApiClient()),
    enabled: supported,
  });

  const registerMu = useMutation({
    mutationFn: async () => {
      const name = window.prompt(
        t('security.passkey.namePrompt'),
        t('security.passkey.defaultName'),
      );
      if (name === null) throw new Error('cancelled');
      const begin = await beginRegisterPasskey(getApiClient());
      // SimpleWebAuthn принимает options в JSON-формате; backend отдаёт
      // именно его. Тип `unknown` снимаем сейчас.
      const opts = begin.options as { publicKey: PublicKeyCredentialCreationOptionsJSON };
      const attestation = await startRegistration({ optionsJSON: opts.publicKey });
      const finish = await finishRegisterPasskey(getApiClient(), {
        sessionToken: begin.sessionToken,
        name: name.trim() || t('security.passkey.defaultName'),
        attestation,
      });
      return finish;
    },
    onSuccess: (res) => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['passkeys'] });
      if (res.recoveryCodes && res.recoveryCodes.length > 0) {
        setPreviewCodes(res.recoveryCodes);
      }
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'cancelled') return;
      const msg =
        err instanceof ApiError && err.code === 'forbidden'
          ? t('security.passkey.disabled')
          : t('security.passkey.error');
      setError(msg);
    },
  });

  const deleteMu = useMutation({
    mutationFn: async (id: string) => {
      if (!window.confirm(t('security.passkey.confirmDelete'))) {
        throw new Error('cancelled');
      }
      await deletePasskey(getApiClient(), id);
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['passkeys'] });
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'cancelled') return;
      if (err instanceof ApiError && err.code === 'forbidden') {
        setError(t('security.passkey.cannotDeleteLast'));
      } else {
        setError(t('security.passkey.error'));
      }
    },
  });

  const regenerateMu = useMutation({
    mutationFn: () => regenerateRecoveryCodes(getApiClient()),
    onSuccess: (res) => {
      setError(null);
      setPreviewCodes(res.recoveryCodes);
    },
    onError: () => setError(t('security.recovery.error')),
  });

  return (
    <section className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <h3 className="mb-1 text-sm font-medium text-slate-900">
        {t('security.section.title')}
      </h3>
      <p className="mb-3 text-xs text-slate-500">{t('security.section.intro')}</p>

      {!supported && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {t('security.passkey.unsupported')}
        </p>
      )}

      {supported && (
        <>
          <div className="mb-4">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('security.passkeys.heading')}
            </h4>
            <PasskeysList
              items={passkeysQuery.data ?? []}
              loading={passkeysQuery.isLoading}
              onDelete={(id) => deleteMu.mutate(id)}
              deletingId={deleteMu.isPending ? (deleteMu.variables as string) : null}
            />
            <button
              type="button"
              disabled={registerMu.isPending}
              onClick={() => registerMu.mutate()}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Fingerprint className="h-4 w-4" />
              {registerMu.isPending
                ? t('security.passkey.registering')
                : t('security.passkey.register')}
            </button>
          </div>

          {error && (
            <p role="alert" className="mb-3 text-xs text-red-600">
              {error}
            </p>
          )}

          <div className="border-t border-slate-200 pt-3">
            <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t('security.recovery.heading')}
            </h4>
            <p className="mb-2 text-xs text-slate-500">{t('security.recovery.intro')}</p>
            <button
              type="button"
              disabled={regenerateMu.isPending}
              onClick={() => regenerateMu.mutate()}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <KeyRound className="h-4 w-4" />
              {regenerateMu.isPending
                ? t('security.recovery.regenerating')
                : t('security.recovery.regenerate')}
            </button>
          </div>
        </>
      )}

      <RecoveryCodesDialog
        open={previewCodes !== null}
        codes={previewCodes ?? []}
        onClose={() => setPreviewCodes(null)}
      />
    </section>
  );
}

function PasskeysList({
  items,
  loading,
  onDelete,
  deletingId,
}: {
  items: Passkey[];
  loading: boolean;
  onDelete: (id: string) => void;
  deletingId: string | null;
}) {
  const t = useT();
  if (loading) {
    return <p className="text-xs text-slate-500">{t('common.loading')}</p>;
  }
  if (items.length === 0) {
    return <p className="text-xs text-slate-500">{t('security.passkeys.empty')}</p>;
  }
  return (
    <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
      {items.map((p) => (
        <li key={p.id} className="flex items-center justify-between px-3 py-2">
          <div>
            <div className="text-sm text-slate-900">{p.name}</div>
            <div className="text-xs text-slate-500">
              {t('security.passkey.created')} {formatDate(p.createdAt)}
              {p.lastUsedAt && (
                <>
                  {' · '}
                  {t('security.passkey.lastUsed')} {formatDate(p.lastUsedAt)}
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onDelete(p.id)}
            disabled={deletingId === p.id}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            aria-label={t('security.passkey.delete')}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t('security.passkey.delete')}
          </button>
        </li>
      ))}
    </ul>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
